import type paper from "paper";
import type { ConstructionLine, OutlineEndpoint, Vec2 } from "../model/Project";

/** What the trace cursor snapped to:
 *  - `intersection` — where two construction lines cross (most specific).
 *  - `anchor`       — a vertex of one construction line.
 *  - `along`        — the nearest point along one construction line. */
export type TraceSnapKind = "anchor" | "intersection" | "along";

export interface TraceSnap {
  point: Vec2;
  /** Construction line ids this point lies on (two for an intersection). */
  lines: string[];
  /** Stable reference to address this point when re-deriving the outline. */
  ref: OutlineEndpoint;
  kind: TraceSnapKind;
  /** The line the cursor is currently nearest to (regardless of the chosen
   *  snap) — answers "which edge am I tracing along right now". */
  nearestLineId: string | null;
}

function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

interface Candidate {
  raw: number;
  eff: number;
  snap: Omit<TraceSnap, "nearestLineId">;
}

/** Find the best construction-geometry snap for the cursor, within
 *  `thresholdMm` (project units). Prefers intersections, then anchors, then
 *  along-line points, by giving the more specific kinds a distance bonus
 *  scaled to the threshold so they win when comparably close. Returns null when
 *  the cursor is off all construction lines. */
export function snapTrace(
  lines: ConstructionLine[],
  cursor: Vec2,
  thresholdMm: number,
  scope: paper.PaperScope,
): TraceSnap | null {
  const cur = new scope.Point(cursor.x, cursor.y);
  const parsed: Array<{
    id: string;
    cp: paper.CompoundPath;
    subs: paper.Path[];
  }> = [];
  for (const l of lines) {
    if (l.visible === false) continue;
    try {
      const cp = new scope.CompoundPath({
        pathData: l.pathData,
        insert: false,
      });
      parsed.push({ id: l.id, cp, subs: subpathsOf(cp) });
    } catch {
      /* skip malformed line */
    }
  }

  try {
    const cands: Candidate[] = [];
    const intersectionBonus = thresholdMm * 0.5;
    const anchorBonus = thresholdMm * 0.34;
    let nearestLineId: string | null = null;
    let nearestLineDist = Infinity;

    for (const p of parsed) {
      for (let si = 0; si < p.subs.length; si++) {
        const sub = p.subs[si]!;
        // Anchors (vertices).
        sub.segments.forEach((seg, segi) => {
          const d = seg.point.getDistance(cur);
          if (d <= thresholdMm) {
            cands.push({
              raw: d,
              eff: d - anchorBonus,
              snap: {
                point: { x: seg.point.x, y: seg.point.y },
                lines: [p.id],
                kind: "anchor",
                ref: {
                  kind: "anchor",
                  constructionId: p.id,
                  pathIndex: si,
                  segmentIndex: segi,
                },
              },
            });
          }
        });
        // Nearest point along this subpath.
        const loc = sub.getNearestLocation(cur);
        if (loc) {
          const d = loc.point.getDistance(cur);
          if (d < nearestLineDist) {
            nearestLineDist = d;
            nearestLineId = p.id;
          }
          if (d <= thresholdMm) {
            cands.push({
              raw: d,
              eff: d,
              snap: {
                point: { x: loc.point.x, y: loc.point.y },
                lines: [p.id],
                kind: "along",
                ref: {
                  kind: "along",
                  constructionId: p.id,
                  pathIndex: si,
                  t: sub.length > 0 ? loc.offset / sub.length : 0,
                },
              },
            });
          }
        }
      }
    }

    // Intersections between every pair of lines.
    for (let i = 0; i < parsed.length; i++) {
      for (let j = i + 1; j < parsed.length; j++) {
        const a = parsed[i]!;
        const b = parsed[j]!;
        let inters: paper.CurveLocation[];
        try {
          inters = a.cp.getIntersections(b.cp);
        } catch {
          continue;
        }
        for (const it of inters) {
          const d = it.point.getDistance(cur);
          if (d <= thresholdMm) {
            cands.push({
              raw: d,
              eff: d - intersectionBonus,
              snap: {
                point: { x: it.point.x, y: it.point.y },
                lines: [a.id, b.id],
                kind: "intersection",
                ref: {
                  kind: "intersection",
                  aId: a.id,
                  bId: b.id,
                  hint: { x: it.point.x, y: it.point.y },
                },
              },
            });
          }
        }
      }
    }

    if (cands.length === 0) return null;
    cands.sort((x, y) => x.eff - y.eff);
    return { ...cands[0]!.snap, nearestLineId };
  } finally {
    for (const p of parsed) p.cp.remove();
  }
}

/** The offset arc direction (`reversed`) and host subpath index for a leg of
 *  `sourceId` between two points, picking the shorter arc on a closed source. */
export function legArcParams(
  line: ConstructionLine,
  startPt: Vec2,
  endPt: Vec2,
  scope: paper.PaperScope,
): { pathIndex: number; reversed: boolean } {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    const subs = subpathsOf(cp);
    // Pick the subpath nearest the start point.
    let best = 0;
    let bestDist = Infinity;
    const sp = new scope.Point(startPt.x, startPt.y);
    subs.forEach((sub, idx) => {
      const loc = sub.getNearestLocation(sp);
      const d = loc ? loc.point.getDistance(sp) : Infinity;
      if (d < bestDist) {
        bestDist = d;
        best = idx;
      }
    });
    const sub = subs[best]!;
    let reversed = false;
    if (sub.closed) {
      const L = sub.length;
      const a = sub.getNearestLocation(sp)?.offset ?? 0;
      const b =
        sub.getNearestLocation(new scope.Point(endPt.x, endPt.y))?.offset ?? 0;
      const fwd = (((b - a) % L) + L) % L;
      reversed = fwd > L / 2; // take the shorter way round
    }
    return { pathIndex: best, reversed };
  } catch {
    return { pathIndex: 0, reversed: false };
  } finally {
    cp?.remove();
  }
}
